import type { TQuestionRule } from 'types/rules.types';

export type RuleChangeAction = { type: string; payload: { resource: Record<string, any> } }; // eslint-disable-line @typescript-eslint/no-explicit-any

export type RuleChangeFacts = {
  /** parameters[nodeId].rules[resource.ruleIdx] AFTER this action's reducer ran — only relevant
   * for customQuestionRuleConditionPlus/customQuestionRuleActionPlus, which mutate a rule in place
   * rather than replacing it wholesale. */
  updatedRuleAtIndex: TQuestionRule | undefined;
};

export type RuleChangePayload = { upsertRules?: TQuestionRule[]; removeRuleIds?: string[] };

// Decides what apply-rule-change should be called with for one rule-editing action, given the
// facts the caller already looked up from Redux state
export function computeRuleChangePayload(
  action: RuleChangeAction,
  actionTypes: {
    customQuestionRuleUpdate: string;
    customQuestionRuleConditionPlus: string;
    customQuestionRuleActionPlus: string;
    deleteCustomQuestionRuleByIndex: string;
    deleteAllCustomRules: string;
  },
  facts: RuleChangeFacts,
): RuleChangePayload | null {
  const resource = action.payload.resource;

  if (action.type === actionTypes.customQuestionRuleUpdate) {
    return { upsertRules: [resource.rule] };
  }

  if (
    action.type === actionTypes.customQuestionRuleConditionPlus ||
    action.type === actionTypes.customQuestionRuleActionPlus
  ) {
    if (!facts.updatedRuleAtIndex) return null;
    return { upsertRules: [facts.updatedRuleAtIndex] };
  }

  if (action.type === actionTypes.deleteCustomQuestionRuleByIndex) {
    const currentRules = resource.currentRules as TQuestionRule[] | undefined;
    const removedRule = currentRules?.[resource.ruleIdx];
    if (!removedRule) return null;
    return { removeRuleIds: [removedRule.id] };
  }

  if (action.type === actionTypes.deleteAllCustomRules) {
    const currentRules = resource.currentRules as TQuestionRule[] | undefined;
    if (!currentRules?.length) return null;
    return { removeRuleIds: currentRules.map((rule) => rule.id) };
  }

  return null;
}
